import { useEffect, useRef } from "react";

import { Badge, Group, Paper, ScrollArea, Stack, UnstyledButton } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";

import {
  DOT_PHRASE_SCOPE_BADGES,
  DOT_PHRASE_SUGGESTION_MAX_HEIGHT,
  DOT_PHRASE_SUGGESTION_SECTION_LABEL,
} from "../../constants/dot-phrases";
import { InsertableDotPhrase } from "../../types/insertable-dot-phrase";

import classes from "./DotPhraseSuggestionList.module.css";

export interface DotPhraseSuggestionListProps {
  dotPhrases: InsertableDotPhrase[];
  selectedIndex: number;
  onSelect: (dotPhrase: InsertableDotPhrase) => void;
  onHighlight?: (index: number) => void;
}

export default function DotPhraseSuggestionList({
  dotPhrases,
  selectedIndex,
  onSelect,
  onHighlight,
}: DotPhraseSuggestionListProps) {
  if (dotPhrases.length === 0) return null;

  return (
    <Paper shadow="md" radius="md" className={classes.popup} data-testid="dot-phrase-suggestions">
      <TextWrapper size="sm" c="gray.6" variant="bold" className={classes.sectionLabel}>
        {DOT_PHRASE_SUGGESTION_SECTION_LABEL}
      </TextWrapper>
      <ScrollArea.Autosize
        type="auto"
        scrollbars="y"
        scrollbarSize={7}
        mah={DOT_PHRASE_SUGGESTION_MAX_HEIGHT}
        classNames={{ viewport: classes.scrollViewport }}
      >
        <div role="listbox" aria-label={DOT_PHRASE_SUGGESTION_SECTION_LABEL}>
          {dotPhrases.map((dotPhrase, index) => (
            <DotPhraseOption
              key={dotPhrase.id}
              dotPhrase={dotPhrase}
              isSelected={index === selectedIndex}
              onSelect={() => onSelect(dotPhrase)}
              onHighlight={() => onHighlight?.(index)}
            />
          ))}
        </div>
      </ScrollArea.Autosize>
    </Paper>
  );
}

interface DotPhraseOptionProps {
  dotPhrase: InsertableDotPhrase;
  isSelected: boolean;
  onSelect: () => void;
  onHighlight: () => void;
}

function DotPhraseOption({ dotPhrase, isSelected, onSelect, onHighlight }: DotPhraseOptionProps) {
  const optionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isSelected) optionRef.current?.scrollIntoView({ block: "nearest" });
  }, [isSelected]);

  const { label, bg, c } = DOT_PHRASE_SCOPE_BADGES[dotPhrase.scope];

  return (
    <UnstyledButton
      role="option"
      ref={optionRef}
      onClick={onSelect}
      aria-selected={isSelected}
      className={classes.option}
      onMouseEnter={onHighlight}
      onMouseDown={(event) => event.preventDefault()}
      data-testid={`dot-phrase-suggestion-${dotPhrase.trigger}`}
    >
      <Group gap="ms" wrap="nowrap" align="center">
        <TextWrapper span size="sm" variant="bold" className={classes.trigger}>
          <span className={classes.truncate}>{dotPhrase.trigger}</span>
        </TextWrapper>
        <Stack gap={0} className={classes.details}>
          <Group gap="xs" wrap="nowrap">
            <TextWrapper variant="bold" className={classes.truncate}>
              {dotPhrase.title}
            </TextWrapper>
            <Badge
              c={c}
              h={24}
              bg={bg}
              fz="sm"
              px="xs"
              fw={600}
              tt="none"
              radius={3}
              className={classes.badge}
            >
              {label}
            </Badge>
          </Group>
          <TextWrapper c="gray.6" className={classes.truncate}>
            {dotPhrase.preview}
          </TextWrapper>
        </Stack>
      </Group>
    </UnstyledButton>
  );
}
